import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LegalPageShell } from "@/components/legal/LegalPageShell";

/**
 * Mind Media — Privacy Policy
 * Route: /privacy
 *
 * Drop-in React component. Plain React + Tailwind core utilities only,
 * no external dependencies, so it works in any Vite/Next/Lovable build.
 *
 * BEFORE PUBLISHING — search this file for "REVIEW:" comments and confirm
 * each one matches what your site actually does. A privacy policy that
 * describes tools you don't use is worse than no policy at all.
 */

const LAST_UPDATED = "August 4, 2026";
const CONTACT_EMAIL = "hello@mindmedia.co";
const MAILING_ADDRESS =
  "1741 Eastlake Pkwy, Ste 102 #2009, Chula Vista, CA 91915";

type SectionProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mt-12 mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
        {title}
      </h2>
      <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="ml-5 list-disc space-y-2 text-muted-foreground marker:text-primary/60">
      {items.map((item, i) => (
        <li key={i} className="pl-1">
          {item}
        </li>
      ))}
    </ul>
  );
}

function PrivacyPage() {
  return (
    <LegalPageShell title="Privacy Policy" lastUpdated={LAST_UPDATED}>


        <div className="mt-8 rounded-lg border border-border bg-muted/40 p-5 text-sm leading-relaxed text-muted-foreground">
          <p className="font-semibold text-foreground">The short version</p>
          <p className="mt-2">
            We collect the contact details you give us through our forms and
            booking links, plus standard analytics about how people use the
            site. We use advertising and analytics tools that set cookies and
            may share limited information with those providers. We do not sell
            your personal information for money. You can ask us to delete what
            we have at any time by emailing{" "}
            <a
              className="font-medium text-foreground underline underline-offset-4"
              href={`mailto:${CONTACT_EMAIL}`}
            >
              {CONTACT_EMAIL}
            </a>
            . The full detail is below, and it controls if anything here is
            ambiguous.
          </p>
        </div>

        <Section id="who-we-are" title="1. Who we are">
          <p>
            Mind Media LLC (&ldquo;Mind Media,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is a Delaware limited
            liability company that designs and builds websites for businesses.
            This policy explains what personal information we collect through{" "}
            <span className="whitespace-nowrap">mindmedia.co</span> and any
            related pages, forms, or booking links we operate, how we use it,
            and what choices you have.
          </p>
          <p>
            For the purposes of privacy laws that use the term, Mind Media is
            the &ldquo;business&rdquo; or &ldquo;controller&rdquo; responsible
            for the personal information described here.
          </p>
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">Contact:</span>{" "}
            <a
              className="underline underline-offset-4"
              href={`mailto:${CONTACT_EMAIL}`}
            >
              {CONTACT_EMAIL}
            </a>
            <br />
            <span className="font-medium text-foreground">Mail:</span>{" "}
            {MAILING_ADDRESS}
          </p>
        </Section>

        <Section
          id="what-we-collect"
          title="2. Information we collect"
        >
          <p className="font-medium text-foreground">
            Information you give us directly
          </p>
          <Bullets
            items={[
              "Contact form and mockup request details: your name, email address, phone number, website URL, Instagram handle, and anything you write in the free-text fields.",
              "Booking details: when you schedule a strategy call, the name, email, phone number, time zone, and any notes you provide to our scheduling tool.",
              "Client and project information: files, images, copy, logins, brand assets, and other materials you send us so we can build your site.",
              "Billing information: your name, billing address, and the details needed to process payment. Full card numbers go directly to our payment processor — we never see or store them.",
              "Correspondence: emails, messages, and call notes exchanged with us.",
            ]}
          />

          <p className="pt-2 font-medium text-foreground">
            Information collected automatically
          </p>
          <Bullets
            items={[
              "Device and connection data: IP address, browser type and version, operating system, device type, and screen size.",
              "Usage data: pages viewed, referring page, links clicked, time spent, scroll depth, and the approximate location derived from your IP address (city or region level, not precise GPS).",
              "Cookies, pixels, and similar technologies, as described in Section 3.",
            ]}
          />

          <p className="pt-2 font-medium text-foreground">
            Information from third parties
          </p>
          <Bullets
            items={[
              "Advertising platforms may tell us that a visit came from a particular campaign, and may report aggregate conversion data back to us.",
              "If you interact with us on social media or through a public business profile, we may receive the information those platforms make available.",
            ]}
          />

          <p className="pt-2">
            We do not intentionally collect sensitive personal information such
            as government ID numbers, precise geolocation, health data,
            biometric data, or racial, religious, or sexual-orientation data,
            and we ask that you not send it to us.
          </p>
        </Section>

        <Section id="cookies" title="3. Cookies and tracking technologies">
          <p>
            We use cookies and similar technologies (pixels, tags, local
            storage, and SDKs) to keep the site working, understand how it is
            used, and measure our advertising. These fall into three groups:
          </p>
          <Bullets
            items={[
              <>
                <span className="font-medium text-foreground">
                  Strictly necessary.
                </span>{" "}
                Required for the site to load, for forms to submit, and for
                basic security. These cannot be turned off through our site.
              </>,
              <>
                <span className="font-medium text-foreground">Analytics.</span>{" "}
                Help us see which pages people visit and where they drop off, so
                we can improve the site. We use Google Analytics for this.
              </>,
              <>
                <span className="font-medium text-foreground">
                  Advertising and conversion measurement.
                </span>{" "}
                Let us measure whether our ads produce inquiries and show
                relevant ads to people who have visited the site. We use the
                Meta (Facebook and Instagram) Pixel and Google advertising tags
                for this.
              </>,
            ]}
          />
          <p>
            {/* REVIEW: Confirm this list matches the tags actually firing on your site.
                Open the site, check your tag manager or view-source, and delete
                anything here you do not actually run. Listing a tool you don't use
                is a misstatement; running one you didn't list is the bigger problem. */}
            Most browsers let you block or delete cookies through their
            settings. Blocking analytics or advertising cookies will not stop
            you from using the site. You can also opt out of Google Analytics
            across all sites using Google&rsquo;s{" "}
            <a
              className="underline underline-offset-4"
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
            >
              browser opt-out add-on
            </a>
            , and manage ad personalization in your{" "}
            <a
              className="underline underline-offset-4"
              href="https://myadcenter.google.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Ad Settings
            </a>{" "}
            and{" "}
            <a
              className="underline underline-offset-4"
              href="https://accountscenter.facebook.com/ad_preferences"
              target="_blank"
              rel="noopener noreferrer"
            >
              Meta ad preferences
            </a>
            .
          </p>
          <p>
            <span className="font-medium text-foreground">
              Global Privacy Control.
            </span>{" "}
            If your browser or extension sends a Global Privacy Control (GPC)
            signal, we treat it as a valid request to opt out of the sharing of
            your personal information for cross-context behavioral advertising
            on that browser.
          </p>
        </Section>

        <Section id="how-we-use" title="4. How we use your information">
          <Bullets
            items={[
              "To respond to your inquiry, prepare a free mockup, and schedule and hold a strategy call.",
              "To design, build, host, update, and support your website, and to provide the services described in our Terms of Service.",
              "To process payments, issue invoices, and manage subscriptions.",
              "To send service messages about your project, your account, billing, downtime, or changes to our terms.",
              "To send marketing emails about our services, where you have asked to hear from us or have an existing business relationship with us. Every marketing email has an unsubscribe link.",
              "To measure and improve our site, our advertising, and the services we offer.",
              "To detect and prevent fraud, abuse, and security incidents.",
              "To comply with legal obligations and to establish, exercise, or defend legal claims.",
            ]}
          />
          <p>
            Where the GDPR applies, we rely on the following legal bases:
            performance of a contract (to deliver the services you hire us for),
            legitimate interests (to run, secure, and improve our business and
            to market to business contacts), consent (for non-essential cookies
            and, where required, marketing email), and legal obligation (for tax
            and accounting records).
          </p>
        </Section>

        <Section id="sharing" title="5. Who we share information with">
          <p>
            We do not sell your personal information for money. We do share it
            with service providers who process it on our behalf, under contracts
            that limit what they can do with it:
          </p>
          <Bullets
            items={[
              "Website hosting, DNS, and content delivery providers.",
              "Email and form delivery providers.",
              "Scheduling and calendar providers, for booking strategy calls.",
              "Payment processors, for billing and subscription management. They handle card data directly under their own privacy policies.",
              "Analytics and advertising providers, as described in Section 3.",
              "Accounting, bookkeeping, and professional advisors.",
            ]}
          />
          {/* REVIEW: If you want to be maximally transparent (and reduce follow-up
              questions from enterprise clients), replace the generic categories above
              with the actual vendor names you use. Naming them is not legally required
              in most states, but it is the version that survives scrutiny. */}
          <p>
            We may also disclose information when required by law, subpoena, or
            valid legal process; to protect our rights, safety, or property or
            that of others; or in connection with a merger, acquisition,
            financing, or sale of all or part of our business, in which case
            this policy will continue to apply to the transferred information
            until it is replaced.
          </p>
          <p>
            <span className="font-medium text-foreground">
              A note on advertising.
            </span>{" "}
            When advertising and analytics cookies are active, information about
            your visit may be transmitted to those platforms in a way that
            California law defines as &ldquo;sharing&rdquo; for cross-context
            behavioral advertising, and that some other state laws define as a
            &ldquo;sale,&rdquo; even though no money changes hands. See Section
            7 for how to opt out.
          </p>
        </Section>

        <Section id="client-data" title="6. Information belonging to our clients">
          <p>
            When we build or maintain a website for you, we may have access to
            personal information belonging to your customers, such as form
            submissions or order records. In that situation we act as a service
            provider or processor for you. We use that information only to
            perform the work you have asked us to do, we do not use it for our
            own marketing, and we return or delete it on your written request
            once it is no longer needed for the engagement.
          </p>
          <p>
            You remain responsible for your own site&rsquo;s privacy notices,
            consent banners, and compliance obligations toward your customers.
            We can build those pages for you, but building them is not legal
            advice and does not transfer that responsibility to us.
          </p>
        </Section>

        <Section id="your-rights" title="7. Your privacy rights">
          <p className="font-medium text-foreground">
            California residents (CCPA/CPRA)
          </p>
          <p>
            If you are a California resident, you have the right to:
          </p>
          <Bullets
            items={[
              "Know what personal information we have collected about you, the categories of sources, the purposes for collecting it, and the categories of third parties we disclose it to.",
              "Delete the personal information we hold about you, subject to legal exceptions such as records we must keep for tax purposes.",
              "Correct inaccurate personal information we hold about you.",
              "Opt out of the sale or sharing of your personal information for cross-context behavioral advertising.",
              "Limit the use of sensitive personal information — although as noted above, we do not collect it.",
              "Not be discriminated against for exercising any of these rights. We will not deny you service, change your price, or lower the quality of your work because you made a privacy request.",
            ]}
          />
          <p>
            To exercise any of these rights, email{" "}
            <a
              className="underline underline-offset-4"
              href={`mailto:${CONTACT_EMAIL}?subject=Privacy%20Request`}
            >
              {CONTACT_EMAIL}
            </a>{" "}
            with the subject line &ldquo;Privacy Request.&rdquo; We will confirm
            receipt within 10 business days and respond within 45 days, or tell
            you if we need up to 45 more. We may ask you to verify your identity
            by confirming details we already hold, such as the email address you
            used to contact us. An authorized agent may submit a request on your
            behalf with written permission signed by you.
          </p>
          <p>
            <span className="font-medium text-foreground">
              To opt out of sharing for advertising:
            </span>{" "}
            enable Global Privacy Control in your browser, reject advertising
            cookies through our cookie controls if shown, or email us at the
            address above with the subject line &ldquo;Do Not Sell or Share My
            Personal Information.&rdquo;
          </p>

          <p className="pt-2 font-medium text-foreground">
            Residents of other US states
          </p>
          <p>
            Residents of states including Virginia, Colorado, Connecticut,
            Utah, Texas, Oregon, and Montana have similar rights to access,
            correct, delete, and obtain a copy of their personal information,
            and to opt out of targeted advertising. We extend these rights to
            all US residents regardless of where you live, and you can exercise
            them the same way. If we deny your request, you may appeal by
            replying to our decision, and we will respond to the appeal within
            60 days.
          </p>

          <p className="pt-2 font-medium text-foreground">
            UK and EEA residents
          </p>
          <p>
            If the UK GDPR or EU GDPR applies to you, you also have the right to
            object to processing based on legitimate interests, to request
            portability of information you provided to us, to withdraw consent
            at any time without affecting prior processing, and to lodge a
            complaint with your local supervisory authority. We are based in the
            United States, so information you send us is transferred to and
            stored in the US. Where required, we rely on the European
            Commission&rsquo;s Standard Contractual Clauses for those transfers.
          </p>
        </Section>

        <Section id="retention" title="8. How long we keep information">
          <p>
            We keep personal information only as long as we need it for the
            purposes described here:
          </p>
          <Bullets
            items={[
              "Inquiries that do not become projects: up to 24 months from your last contact with us, so we can pick up the conversation if you come back.",
              "Client project records and correspondence: for the life of the engagement plus 4 years after it ends, to cover warranty questions and the statute of limitations on contract claims.",
              "Billing and tax records: 7 years, as required by tax rules.",
              "Analytics data: per the retention settings of our analytics provider, typically 14 months.",
            ]}
          />
          <p>
            After those periods we delete the information or aggregate it so it
            can no longer identify you.
          </p>
        </Section>

        <Section id="security" title="9. Security">
          <p>
            We use reasonable administrative and technical safeguards to protect
            personal information, including encryption in transit (HTTPS),
            access controls on the accounts that hold client data, and
            multi-factor authentication on our critical systems. No method of
            transmission or storage is completely secure, and we cannot
            guarantee absolute security. If a breach affects your personal
            information, we will notify you and any required regulator within
            the timeframes the law requires.
          </p>
          <p>
            Please do not send passwords, API keys, or other credentials by
            plain email. Ask us for a secure method and we will provide one.
          </p>
        </Section>

        <Section id="children" title="10. Children">
          <p>
            Our site and services are for businesses and are not directed to
            children. We do not knowingly collect personal information from
            anyone under 16. If you believe a child has provided us information,
            email us and we will delete it.
          </p>
        </Section>

        <Section id="third-party" title="11. Third-party sites">
          <p>
            Our site links to other sites, including client websites we have
            built and social media profiles. We are not responsible for the
            privacy practices of those sites. Read their policies before
            providing them information.
          </p>
        </Section>

        <Section id="changes" title="12. Changes to this policy">
          <p>
            We may update this policy as our business or the law changes. When
            we do, we will change the &ldquo;Last updated&rdquo; date at the top
            of this page. If the changes are material — for example, if we begin
            using personal information for a genuinely new purpose — we will
            give notice by email or by a notice on the site before the change
            takes effect. Continuing to use the site after an update means you
            accept the revised policy.
          </p>
        </Section>

        <Section id="contact" title="13. Contact us">
          <p>
            Questions, requests, or complaints about privacy can go to:
          </p>
          <p className="rounded-lg border border-border bg-muted/40 p-5">
            <span className="font-medium text-foreground">Mind Media LLC</span>
            <br />
            <a
              className="underline underline-offset-4"
              href={`mailto:${CONTACT_EMAIL}`}
            >
              {CONTACT_EMAIL}
            </a>
            <br />
            {MAILING_ADDRESS}
          </p>
          <p className="text-sm text-muted-foreground">
            We aim to respond to every privacy request within 5 business days,
            and always within the timeframes the law requires.
          </p>
        </Section>

    </LegalPageShell>

  );
}

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Mind Media" },
      { name: "description", content: "How Mind Media collects, uses, and protects the information you share through our forms, booking links, and website analytics." },
      { property: "og:title", content: "Privacy Policy — Mind Media" },
      { property: "og:description", content: "How Mind Media collects, uses, and protects the information you share through our forms, booking links, and website analytics." },
      { property: "og:url", content: "https://mindmedia.co/privacy" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/privacy" }],
  }),
  component: PrivacyPage,
});
